import { useEffect } from 'react';

const STORAGE_KEY = 'northai-settings';

// Shared with App.js's settings state, which is the only writer of this key
// - read-only here so SubscribePage (a separate top-level route, see
// index.js, that never mounts App and so never runs its settings effects)
// can still honor whatever theme the user picked.
export function getStoredTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved && JSON.parse(saved).theme) || 'light';
  } catch {
    return 'light';
  }
}

// Browser chrome follows the page - in the Android app (TWA) this is what
// colors the status bar, so it matches the app's own header: same values as
// themeColor / themeColorDark in the Android project's twa-manifest.json.
// barColor overrides it for screens with their own fixed background.
function setResolvedTheme(resolved, barColor) {
  document.documentElement.setAttribute('data-theme', resolved);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', barColor || (resolved === 'dark' ? '#1C1917' : '#FDFAF4'));
}

// "system" follows the OS/browser theme (prefers-color-scheme) instead of a
// fixed choice - every dark-mode rule in App.css/SubscribePage.css keys off
// the data-theme attribute on <html>, not prefers-color-scheme directly, so
// "system" is resolved here rather than left to a CSS media query. Returns
// a cleanup function that removes the OS-change listener, if one was added.
export function applyTheme(theme, barColor = null) {
  if (theme !== 'system') {
    setResolvedTheme(theme, barColor);
    return () => {};
  }

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const apply = () => setResolvedTheme(media.matches ? 'dark' : 'light', barColor);
  apply();
  media.addEventListener('change', apply);
  return () => media.removeEventListener('change', apply);
}

export function useTheme(theme, barColor = null) {
  useEffect(() => applyTheme(theme, barColor), [theme, barColor]);
}
